import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from "js-cookie";

import { getActivities } from "api/activities";

import { useUser } from "./user";

import LoadingContainer from "@parkagent/ui/dist/components/LoadingContainer";

import pages from "routes/main";
import { getReward } from "api/rewards";

const Context = createContext({});

export function useActivity() {
    return useContext(Context);
}

const publicPages = pages?.reduce((acc, {routes} = {}) => {
    return [
        ...acc,
        ...routes?.filter(({private: isPrivate}) => !isPrivate).map(({path}) => path) || []
    ]
}, []) || [];

export function ActivitiesContext(props) {

    const { isLoggedIn, userId } = useUser();
    const [activity, setActivity] = useState();
    const cache = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();

    const {data, isLoading, refetch, isError} = useQuery({
        queryKey: ["activities"],
        queryFn: () => getActivities({
            params: {
                user: userId,
                type: "agencies",
                sort: {
                    name: 1
                },
                limit: 100
            }
        }),
        enabled: !!isLoggedIn,
        refetchOnMount: false,
        refetchOnWindowFocus: false
    })

    const { data: reward } = useQuery({
        queryKey: ["rewards", activity?._id],
        queryFn: getReward,
        enabled: !!activity?._id,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 0
    });

    useEffect(() => {
        const previusActivity = Cookies.get('activity-id');
        let activityToSet = data?.[0];
        if(!!previusActivity && activityToSet?._id !== previusActivity) {
            activityToSet = data?.find(activity => activity._id === previusActivity);
        }
        if(!activityToSet) return;
        return setFirstActivity(activityToSet?._id);
    }, [data])

    function changeActivity(activityId) {
        const newActivity = data?.find(activity => activity._id === activityId);
        if(!newActivity) return;
        Cookies.set('organization-id', newActivity?.owner)
        Cookies.set('activity-id', newActivity?._id)
        setActivity(newActivity);
        cache.invalidateQueries([])
        return navigate('/');
    }

    function setFirstActivity(activityId) {
        const newActivity = data?.find(activity => activity._id === activityId);
        if(!newActivity) return;
        setActivity(newActivity);
        Cookies.set('organization-id', newActivity?.owner)
        Cookies.set('activity-id', activityId)
        return;
    }

    const isPublic = publicPages.includes(location.pathname);

    if(isError && !isPublic) {
        return <Navigate replace to={`/activity-not-found`} />
    }

    const hasMonthlyBilling = activity?.options?.some(option => option.key === 'PAYMENT_MONTHLY' && option.value === true);

    return (
        <Context.Provider
            value={{
                activity,
                activities: data || [],
                activityId: activity?._id,
                options: activity?.options?.reduce((acc, option) => {
                    acc[option.key] = option.value;
                    return acc;
                }, {}) || {},
                hasMonthlyBilling,
                isLoading,
                changeActivity,
                setFirstActivity,
                refetch,
                reward: {
                    points: reward?.points || 0,
                    missing: 12000 - (reward?.points || 0),
                    coupon: reward?.coupon,
                    available: reward?.points >= 12000,
                    coupons: reward?.value && reward?.coupon ? Math.floor(reward?.value / reward?.coupon) : 0,
                }
            }}
        >
            <LoadingContainer isLoading={isLoading}>
                {props.children}
            </LoadingContainer>
        </Context.Provider>
    )
}